import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { audienceService } from '../../services';
import { useAuth } from '../../context/AuthContext';
import { useDialog } from '../../context/DialogContext';
import { ClubPanel, PanelHead, PillButton, TagPill, EmptyBlock, useArmed, useToast, Toast, MUTED, CHIP, RULE } from '../club/clubUi';
import LoadingSpinner from '../LoadingSpinner';
import EmailBuilder from '../broadcast/EmailBuilder';
import SmsBuilder from '../broadcast/SmsBuilder';
import NewSendPanel from './NewSendPanel';

const STATUS_PILL = { Draft: [CHIP, '#000'], Scheduled: ['#FACC15', '#000'], Sending: ['#00FFFF', '#000'], Sent: ['#000', '#fff'], Failed: ['#DC2626', '#fff'], Paused: ['#EAB308', '#000'] };
const CHANNEL_LABEL = { sms: 'TEXT', email: 'EMAIL', notification: 'NOTIFICATION' };
const AUDIENCE_LABEL = (kind, followerWord) => (kind === 'followers' ? `ALL ${followerWord}` : kind === 'everyone' ? 'EVERYONE' : kind === 'people' ? 'PICKED PEOPLE' : 'CONTACT LIST');

/** Whether the SEND tab is showing a builder — the manage page drops its rail when it is. */
export function useBroadcastTakeover() {
  const [params] = useSearchParams();
  return !!params.get('send');
}

/**
 * SEND (design_handoff_club_pages/README.md §2) — NEW SEND on top (notification · email · text
 * to members, the list, a group or picked people), then SENT: every send and draft. Email and
 * text drafts open the builder as a full-width takeover (`?send=<id|new>&ch=email|sms`) so a
 * reload lands back in it. `owner` is { ownerType, ownerId, name, publicPath, followerWord };
 * `perms` is { canEdit, canCreate, canSend }. `initialPick` ({ userIds, contactIds }) comes
 * from SEND TO THESE → on the contact list.
 */
export default function BroadcastsPanel({ owner, perms, mobile = false, initialPick = null }) {
  const { ownerType, ownerId } = owner;
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const { showAlert } = useDialog();
  const [rows, setRows] = useState(null);
  const [state, setState] = useState({});
  const { armed, confirm, disarm } = useArmed();
  const { toast, say } = useToast();

  const openId = params.get('send');
  const channel = params.get('ch') === 'sms' ? 'sms' : 'email';
  const client = useMemo(() => audienceService.broadcastClient(ownerType, ownerId), [ownerType, ownerId]);

  const load = useCallback(async () => {
    const r = await audienceService.listBroadcasts(ownerType, ownerId);
    setRows(r.success ? (r.data || []) : []);
  }, [ownerType, ownerId]);
  useEffect(() => { if (!openId) load(); }, [load, openId]);

  const open = (id, ch) => {
    const next = new URLSearchParams(params);
    next.set('send', id); next.set('ch', ch === 'sms' ? 'sms' : 'email');
    next.delete('pick'); next.delete('pickUsers');
    setParams(next);
  };
  const close = () => {
    const next = new URLSearchParams(params);
    next.delete('send'); next.delete('ch');
    setParams(next);
  };
  const remove = async (n) => {
    if (!confirm(`del-${n.id}`)) return;
    const r = await client.remove(n.id);
    if (!r.success) await showAlert({ title: 'COULD NOT DELETE', message: r.error || 'Try again.', type: 'error' });
    load();
  };

  const page = { ownerType, ownerId, name: owner.name, publicPath: owner.publicPath, followerWord: owner.followerWord, logoUrl: owner.logoUrl || null, address: owner.address || null };

  if (openId) {
    const isSms = channel === 'sms';
    const label = openId === 'new' ? (isSms ? 'NEW TEXT' : 'NEW EMAIL') : (state.subject || 'UNTITLED').toUpperCase();
    const saved = openId === 'new' ? 'Not saved yet' : state.dirty ? 'Unsaved changes' : state.updatedAt ? `Saved ${new Date(state.updatedAt).toLocaleString()}` : '';
    return (
      <div className="flex flex-col font-mono" style={{ gap: 16 }} data-testid="broadcast-takeover">
        {isSms ? (
          <>
            <div className="flex flex-wrap items-center bg-white" style={{ gap: 12, padding: '10px 12px', borderRadius: 999 }}>
              <PillButton variant="tertiary" size={34} weight={800} onClick={close}>← ALL SENDS</PillButton>
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase' }}>{label} · {String(state.status || 'Draft').toUpperCase()}</span>
              {!state.loading && saved && <span style={{ fontSize: 11, color: MUTED }}>{saved}</span>}
            </div>
            <SmsBuilder id={openId} client={client} perms={perms} page={page} onSavedNew={(id) => open(id, 'sms')} onState={setState} testTarget={user?.phoneNumber ? 'your phone' : 'your phone (verify it in Settings first)'} stacked={mobile} />
          </>
        ) : (
          // Email Studio carries its own ← ALL SENDS row and header card.
          <EmailBuilder id={openId} client={client} perms={perms} mode="page" page={page} onSavedNew={(id) => open(id, 'email')} onState={setState} testTarget={user?.email || 'your inbox'} onBack={close} roleTag={owner.roleTag || 'OWNER'} />
        )}
      </div>
    );
  }

  const when = (n) => {
    const d = n.sentAt || n.scheduledFor || n.updatedAt || n.createdAt;
    return d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }).toUpperCase() : '';
  };
  const meta = (n) => [when(n), AUDIENCE_LABEL(n.audienceKind, owner.followerWord), n.sentAt ? `${Number(n.recipientCount || 0).toLocaleString()} REACHED` : n.scheduledFor ? `GOES ${new Date(n.scheduledFor).toLocaleString()}` : String(n.status || 'DRAFT').toUpperCase()].filter(Boolean).join(' · ');
  const title = (n) => (n.channel === 'email' ? (n.subject || '(no subject)') : (n.smsBody || n.subject || '(empty)'));

  return (
    <div className="flex flex-col" style={{ gap: mobile ? 10 : 16 }} data-testid="broadcasts-panel">
      {perms.canCreate !== false && (
        <NewSendPanel owner={owner} perms={perms} mobile={mobile} initialPick={initialPick} onOpenBuilder={open} onSent={load} onToast={say} />
      )}
      <ClubPanel phone={mobile} style={{ gap: 6 }} data-testid="broadcast-list">
        <PanelHead title="SENT" right={rows == null ? '' : rows.length === 0 ? 'NOTHING YET' : `${rows.length} ${rows.length === 1 ? 'SEND' : 'SENDS'}`} />
        {rows === null ? (
          <div className="py-6 text-center"><LoadingSpinner size="sm" /></div>
        ) : rows.length === 0 ? (
          <EmptyBlock title="NOTHING SENT YET." body="Your first notification, email or text shows up here with who it went to and when." minHeight={120} />
        ) : rows.map((n, i) => {
          const [bg, fg] = STATUS_PILL[n.status] || STATUS_PILL.Draft;
          const canOpen = n.channel !== 'notification';
          const Row = canOpen ? 'button' : 'span';
          return (
            <div key={n.id} className="flex flex-wrap items-center" style={{ gap: '8px 14px', padding: '12px 0', borderTop: i ? RULE : 'none' }} data-testid="broadcast-row">
              <Row type={canOpen ? 'button' : undefined} onClick={canOpen ? () => open(n.id, n.channel) : undefined} className="flex min-w-0 flex-col text-left" style={{ flex: '1 1 220px', gap: 4, border: 'none', background: 'none', padding: 0, fontFamily: 'inherit', cursor: canOpen ? 'pointer' : 'default', color: 'inherit' }}>
                <span className="truncate" style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.4 }}>{title(n)}</span>
                <span style={{ fontSize: 10, letterSpacing: '.1em', color: MUTED, textTransform: 'uppercase' }}>{meta(n)}</span>
              </Row>
              <span className="flex flex-none items-center" style={{ gap: 6 }}>
                <TagPill bg={CHIP}>{CHANNEL_LABEL[n.channel] || String(n.channel).toUpperCase()}</TagPill>
                {n.status !== 'Sent' && <TagPill bg={bg} fg={fg}>{String(n.status).toUpperCase()}</TagPill>}
                {n.status === 'Draft' && perms.canEdit && (
                  <PillButton variant={armed === `del-${n.id}` ? 'armed' : 'danger'} size={30} onClick={() => remove(n)} onBlur={disarm} aria-label="Delete draft">{armed === `del-${n.id}` ? 'CONFIRM' : '✕'}</PillButton>
                )}
              </span>
            </div>
          );
        })}
      </ClubPanel>
      <Toast text={toast} phone={mobile} />
    </div>
  );
}
